import {
	DEFAULT_DND_STEP_LENGTH,
	DEFAULT_HOUR_HEIGHT,
	DEFAULT_SPACE_HEIGHT,
	DEFAULT_SPACE_WIDTH,
	IOptions,
	MIN_HOUR_HEIGHT,
	MIN_SPACE_HEIGHT,
	MIN_SPACE_WIDTH,
} from '@bondsports/utils';

const CALENDAR_MODE_DAILY = 'daily';
const CALENDAR_MODE_DAYX = 'dayx';
const CALENDAR_MODE_WEEKLY = 'weekly';
const CALENDAR_MODE_MONTHLY = 'monthly';
const CALENDAR_VIEW_HORIZONTAL = 'horizontal';

export type CalendarGridLineMinutes = 0 | 5 | 15 | 30 | 45 | 60;
export type CalendarDndStepMinutes = 5 | 10 | 15 | 30 | 45 | 60;

export interface ICalendarViewPrefs {
	timeDensityPercent: number;
	resourceDensityPercent: number;
	gridLineMinutes: CalendarGridLineMinutes;
	dndStepMinutes: CalendarDndStepMinutes;
}

export const DEFAULT_CALENDAR_DENSITY_PERCENT = 100;
export const DEFAULT_CALENDAR_GRID_LINE_MINUTES: CalendarGridLineMinutes = 0;
export const DEFAULT_CALENDAR_DND_STEP_MINUTES: CalendarDndStepMinutes = DEFAULT_DND_STEP_LENGTH as CalendarDndStepMinutes;
export const CALENDAR_TIME_DENSITY_OPTIONS = [50, 75, 100, 125, 150] as const;
export const CALENDAR_RESOURCE_DENSITY_OPTIONS = [25, 50, 75, 100, 125, 150] as const;
export const CALENDAR_GRID_LINE_OPTIONS: CalendarGridLineMinutes[] = [0, 5, 15, 30, 45, 60];
export const CALENDAR_DND_STEP_OPTIONS: CalendarDndStepMinutes[] = [5, 10, 15, 30, 45, 60];
const PERCENT_BASE = 100;

const prefsKey = (organizationId: number, userId: number, facilityId: number): string =>
	`calendar-view-prefs:organization:${organizationId}-user:${userId}-facility:${facilityId}`;

const snapToOptions = (value: number, options: readonly number[]): number => {
	const clamped = Math.min(options[options.length - 1], Math.max(options[0], value));
	return options.reduce((closest, option) =>
		Math.abs(option - clamped) < Math.abs(closest - clamped) ? option : closest
	);
};

export const normalizeTimeDensityPercent = (value: number): number =>
	snapToOptions(value, CALENDAR_TIME_DENSITY_OPTIONS);
export const normalizeResourceDensityPercent = (value: number): number =>
	snapToOptions(value, CALENDAR_RESOURCE_DENSITY_OPTIONS);
export const normalizeDndStepMinutes = (value: number): CalendarDndStepMinutes =>
	snapToOptions(value, CALENDAR_DND_STEP_OPTIONS) as CalendarDndStepMinutes;

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const isGridLineMinutes = (value: unknown): value is CalendarGridLineMinutes =>
	CALENDAR_GRID_LINE_OPTIONS.includes(value as CalendarGridLineMinutes);

/** Default calendar view prefs (100% / 100% / None / 5 min). */
export const getDefaultCalendarViewPrefs = (): ICalendarViewPrefs => ({
	timeDensityPercent: DEFAULT_CALENDAR_DENSITY_PERCENT,
	resourceDensityPercent: DEFAULT_CALENDAR_DENSITY_PERCENT,
	gridLineMinutes: DEFAULT_CALENDAR_GRID_LINE_MINUTES,
	dndStepMinutes: DEFAULT_CALENDAR_DND_STEP_MINUTES,
});

const normalizePrefs = (prefs: Partial<ICalendarViewPrefs>): ICalendarViewPrefs => ({
	timeDensityPercent: isFiniteNumber(prefs.timeDensityPercent)
		? normalizeTimeDensityPercent(prefs.timeDensityPercent)
		: DEFAULT_CALENDAR_DENSITY_PERCENT,
	resourceDensityPercent: isFiniteNumber(prefs.resourceDensityPercent)
		? normalizeResourceDensityPercent(prefs.resourceDensityPercent)
		: DEFAULT_CALENDAR_DENSITY_PERCENT,
	gridLineMinutes: isGridLineMinutes(prefs.gridLineMinutes)
		? prefs.gridLineMinutes
		: DEFAULT_CALENDAR_GRID_LINE_MINUTES,
	dndStepMinutes: isFiniteNumber(prefs.dndStepMinutes)
		? normalizeDndStepMinutes(prefs.dndStepMinutes)
		: DEFAULT_CALENDAR_DND_STEP_MINUTES,
});

/** Loads validated calendar view prefs from localStorage. */
export function loadCalendarViewPrefs(
	organizationId: number,
	userId: number,
	facilityId: number
): ICalendarViewPrefs {
	try {
		const raw = localStorage.getItem(prefsKey(organizationId, userId, facilityId));
		if (!raw) return getDefaultCalendarViewPrefs();
		const parsed: unknown = JSON.parse(raw);
		if (typeof parsed !== 'object' || parsed === null) return getDefaultCalendarViewPrefs();
		return normalizePrefs(parsed as Partial<ICalendarViewPrefs>);
	} catch (error) {
		console.warn('Failed to load calendar view prefs', error);
		return getDefaultCalendarViewPrefs();
	}
}

/** Persists normalized calendar view prefs. */
export function saveCalendarViewPrefs(
	organizationId: number,
	userId: number,
	facilityId: number,
	prefs: ICalendarViewPrefs
): void {
	try {
		localStorage.setItem(prefsKey(organizationId, userId, facilityId), JSON.stringify(normalizePrefs(prefs)));
	} catch (error) {
		console.warn('Failed to save calendar view prefs', error);
	}
}

/** Weekly/monthly have no hour axis — Hours density would be a no-op. */
export const shouldApplyTimeDensity = (options: Pick<IOptions, 'mode'>): boolean =>
	options.mode !== CALENDAR_MODE_WEEKLY && options.mode !== CALENDAR_MODE_MONTHLY;

/** Monthly has no resource columns; weekly horizontal / Resource (day-x) density is unreliable. */
export const shouldApplyResourceDensity = (options: Pick<IOptions, 'mode' | 'view'>): boolean =>
	options.mode !== CALENDAR_MODE_MONTHLY &&
	options.mode !== CALENDAR_MODE_DAYX &&
	!(options.mode === CALENDAR_MODE_WEEKLY && options.view === CALENDAR_VIEW_HORIZONTAL);

/** Gridlines and grid increments only apply on daily / day-x time grids. */
export const shouldApplyTimeGridPrefs = (options: Pick<IOptions, 'mode'>): boolean =>
	options.mode === CALENDAR_MODE_DAILY || options.mode === CALENDAR_MODE_DAYX;

/** Applies Hours/Resources/Gridlines/Grid increments onto options. */
export function applyCalendarViewOptions(options: IOptions, prefs: ICalendarViewPrefs): IOptions {
	const timePercent = shouldApplyTimeDensity(options)
		? normalizeTimeDensityPercent(prefs.timeDensityPercent)
		: DEFAULT_CALENDAR_DENSITY_PERCENT;
	const resourcePercent = shouldApplyResourceDensity(options)
		? normalizeResourceDensityPercent(prefs.resourceDensityPercent)
		: DEFAULT_CALENDAR_DENSITY_PERCENT;
	const hourSize = Math.max(MIN_HOUR_HEIGHT, Math.round((DEFAULT_HOUR_HEIGHT * timePercent) / PERCENT_BASE));
	return {
		...options,
		hourSize: { vertical: hourSize, horizontal: hourSize },
		resourceSize: {
			width: Math.max(MIN_SPACE_WIDTH, Math.round((DEFAULT_SPACE_WIDTH * resourcePercent) / PERCENT_BASE)),
			height: Math.max(MIN_SPACE_HEIGHT, Math.round((DEFAULT_SPACE_HEIGHT * resourcePercent) / PERCENT_BASE)),
		},
		gridLineStepMinutes: shouldApplyTimeGridPrefs(options)
			? prefs.gridLineMinutes
			: DEFAULT_CALENDAR_GRID_LINE_MINUTES,
		dndStepLength: shouldApplyTimeGridPrefs(options)
			? normalizeDndStepMinutes(prefs.dndStepMinutes)
			: DEFAULT_CALENDAR_DND_STEP_MINUTES,
	};
}
